import axios from 'axios';

const defaultState = () => {
  return {
    startDate: null,
    endDate: null,
    summary: undefined,
    customerIncome: undefined,
    customerLoan: undefined,
    customerRisk: undefined,
    customerExpenditure: undefined,
    customerCashflow: undefined,
    transactions: undefined,
    transactionsCategoriesList: undefined,
    granularInsights: {
      totals: {
        credits_total: undefined,
        debits_total: undefined,
      },
      transactionsByTimePeriod: undefined,
      transactionsByMerchant: undefined,
      transactionsByCategory: undefined,
    },
    creditInsightsByMonth: undefined,
  };
};

const errorState = (error) => {
  return {
    isInscribeAPIError: true,
    error: error,
  };
};

export const isErrorResponse = (response) => {
  return response.isInscribeAPIError;
};

export default {
  state: defaultState(),
  mutations: {
    clearCreditData(state) {
      Object.assign(state, defaultState());
    },
    setStartDate(state, configData) {
      state.startDate = configData;
    },
    setEndDate(state, configData) {
      state.endDate = configData;
    },
    SET_SUMMARY(state, configData) {
      state.summary = configData;
    },
    SET_CUSTOMER_INCOME(state, configData) {
      state.customerIncome = configData;
    },
    SET_CUSTOMER_LOAN(state, configData) {
      state.customerLoan = configData;
    },
    SET_CUSTOMER_RISK(state, configData) {
      state.customerRisk = configData;
    },
    SET_CUSTOMER_EXPENDITURE(state, configData) {
      state.customerExpenditure = configData;
    },
    SET_CUSTOMER_CASHFLOW(state, configData) {
      state.customerCashflow = configData;
    },
    SET_TRANSACTIONS(state, configData) {
      state.transactions = configData;
    },
    SET_INSIGHTS_TOTAL_TRANSACTIONS_BY_TIME_PERIOD(state, configData) {
      state.granularInsights.transactionsByTimePeriod = configData;
    },
    SET_INSIGHTS_TOP_TRANSACTIONS_BY_MERCHANT(state, configData) {
      state.granularInsights.transactionsByMerchant = configData;
    },
    SET_INSIGHTS_TOP_TRANSACTIONS_BY_CATEGORY(state, configData) {
      state.granularInsights.transactionsByCategory = configData;
    },
    SET_INSIGHTS_TOTAL_CREDIT_AMOUNT(state, amount) {
      state.granularInsights.totals.credits_total = amount;
    },
    SET_INSIGHTS_TOTAL_DEBIT_AMOUNT(state, amount) {
      state.granularInsights.totals.debits_total = amount;
    },
    SET_TRANSACTIONS_CATEGORIES_LIST(state, list) {
      state.transactionsCategoriesList = list;
    },
    SET_CREDIT_INSIGHTS_BY_MONTH_LIST(state, list) {
      state.creditInsightsByMonth = list;
    },
  },
  getters: {
    insightsTotalTransactionsByTimePeriod: (state) => {
      return state.granularInsights.transactionsByTimePeriod.data;
    },
    insightsTopTransactionsByMerchant: (state) => {
      return state.granularInsights.transactionsByMerchant.data;
    },
    insightsTopTransactionsByCategory: (state) => {
      return state.granularInsights.transactionsByCategory.data;
    },
    insightsTotals: (state) => {
      return state.granularInsights.totals;
    },
    transactionsCategories: (state) => {
      return state.transactionsCategoriesList;
    },
  },
  actions: {
    async getCreditSummary({ commit }, { customerId, queryParams }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis`,
        { params: queryParams },
      );
      commit('SET_SUMMARY', response.data);
    },
    async getCustomerIncomeInsights({ commit }, { customerId, queryParams }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis/income_insights`,
        { params: queryParams },
      );
      commit('SET_CUSTOMER_INCOME', response.data);
    },
    async getCustomerLoanInsights({ commit }, { customerId, queryParams }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis/loan_insights`,
        { params: queryParams },
      );
      commit('SET_CUSTOMER_LOAN', response.data);
    },
    async getCustomerRiskInsights({ commit }, { customerId, queryParams }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis/risk_insights`,
        { params: queryParams },
      );
      commit('SET_CUSTOMER_RISK', response.data);
    },
    async getCustomerExpenditureInsights(
      { commit },
      { customerId, queryParams },
    ) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis/expenditure_insights`,
        { params: queryParams },
      );
      commit('SET_CUSTOMER_EXPENDITURE', response.data);
    },
    async getCustomerCashflowInsights({ commit }, { customerId, queryParams }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/credit_analysis/cashflow_insights`,
        { params: queryParams },
      );
      commit('SET_CUSTOMER_CASHFLOW', response.data);
    },
    async getCreditInsightsByMonth({ commit }, { customerId, queryParams }) {
      await axios
        .get(`/api/v2/customers/${customerId}/credit_insights_by_month`, {
          params: queryParams,
        })
        .then((response) => {
          commit('SET_CREDIT_INSIGHTS_BY_MONTH_LIST', response.data);
        })
        .catch((error) => {
          commit('SET_CREDIT_INSIGHTS_BY_MONTH_LIST', errorState(error));
        });
    },
    async getCustomerTransactions({ commit }, { customerId, params }) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/transactions?${params}`,
      );
      commit('SET_TRANSACTIONS', response.data);
    },
    async getCustomerTransactionsExportsCsv({}, { customerId, params }) {
      return await axios.get(
        `/api/v2/customers/${customerId}/transactions/exports/csv`,
        { params },
      );
    },
    async getCustomerTransactionsByTimePeriod(
      { commit },
      { customerId, queryParams },
    ) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/transactions/grouped_by_time_period`,
        { params: queryParams },
      );
      commit('SET_INSIGHTS_TOTAL_TRANSACTIONS_BY_TIME_PERIOD', response.data);
      commit('SET_INSIGHTS_TOTAL_CREDIT_AMOUNT', response.data.credits_total);
      commit('SET_INSIGHTS_TOTAL_DEBIT_AMOUNT', response.data.debits_total);
    },
    async getCustomerTransactionsByMerchant(
      { commit },
      { customerId, queryParams },
    ) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/transactions/grouped_by_merchant`,
        {
          params: queryParams,
        },
      );

      commit('SET_INSIGHTS_TOP_TRANSACTIONS_BY_MERCHANT', response.data);
    },
    async getCustomerTransactionsByCategory(
      { commit },
      { customerId, queryParams },
    ) {
      const response = await axios.get(
        `/api/v2/customers/${customerId}/transactions/grouped_by_category`,
        {
          params: queryParams,
        },
      );

      commit('SET_INSIGHTS_TOP_TRANSACTIONS_BY_CATEGORY', response.data);
    },
    async getCustomerTransactionsCategoriesList({ commit }) {
      const response = await axios.get(`/api/v2/transactions/categories/`);
      const categories = response.data.data;
      commit('SET_TRANSACTIONS_CATEGORIES_LIST', categories);
    },
    resetGranularInsights({ commit }) {
      commit('SET_INSIGHTS_TOTAL_TRANSACTIONS_BY_TIME_PERIOD', undefined);
      commit('SET_INSIGHTS_TOP_TRANSACTIONS_BY_MERCHANT', undefined);
      commit('SET_INSIGHTS_TOP_TRANSACTIONS_BY_CATEGORY', undefined);
      commit('SET_INSIGHTS_TOTAL_CREDIT_AMOUNT', undefined);
      commit('SET_INSIGHTS_TOTAL_DEBIT_AMOUNT', undefined);
    },
    setCustomerTransactionCategory(
      {},
      { customerId, transactionIds, category },
    ) {
      return axios.patch(
        `/api/v2/customers/${customerId}/transactions/categories/${category}`,
        {
          transaction_ids: transactionIds,
        },
      );
    },
    sendAgentQuery({}, { customerId, query }) {
      return axios.post(
        `/api/v2/customers/${customerId}/credit_analysis/agent`,
        {
          question: query,
        },
      );
    },
  },
};
